// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// API
import { deleteClinicVideo } from "../../api";

export const useDeleteClinicVideo = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (videoId: string) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return deleteClinicVideo(token, clinicId, videoId);
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (_) => {
      queryClient.invalidateQueries({
        queryKey: ["clinic-videos", clinicId],
      });
      queryClient.invalidateQueries({
        queryKey: ["clinic-packages", clinicId],
      });
      queryClient.invalidateQueries({ queryKey: ["exams"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled-exams"] });
      queryClient.invalidateQueries({ queryKey: ["linked-exams"] });

      setSnackbarInfo({
        variant: "success",
        message: t("Video deleted successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Video could not be deleted!"),
      });
    },
  });
};
